
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";

interface ProfilePrice {
  id: string;
  profile_report_id: string;
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
  grade: string | null;
  package_type: string | null;
  origin: string | null;
  created_at: string;
  updated_at: string;
}

interface PriceTableRowProps {
  price: ProfilePrice;
  isFirstInGroup: boolean;
}

export const PriceTableRow = ({ price, isFirstInGroup }: PriceTableRowProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getPriceColor = (price: number) => {
    if (price > 5) return 'text-green-600';
    if (price > 2) return 'text-blue-600';
    return 'text-yellow-600';
  };

  const getPriceTrend = (price: number) => {
    // Mock trend calculation based on price ranges
    const trend = Math.random() > 0.5 ? 'up' : 'down';
    const percentage = (Math.random() * 10 + 1).toFixed(1);
    return { trend, percentage };
  };

  const { trend, percentage } = getPriceTrend(price.price);

  return (
    <TableRow className={isFirstInGroup ? 'border-t-2 border-gray-200' : ''}>
      <TableCell>
        <div>
          <div className="font-semibold text-gray-900">
            {price.commodity_name}
          </div>
          {price.variety && (
            <div className="text-sm text-blue-600">
              {price.variety}
            </div>
          )}
          {price.group_name && (
            <Badge variant="secondary" className="text-xs mt-1">
              {price.group_name}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <span className={`text-lg font-bold flex items-center ${getPriceColor(price.price)}`}>
            <DollarSign size={16} />
            {price.price.toFixed(2)}
          </span>
          <div className="flex items-center space-x-1 text-sm">
            {trend === 'up' ? (
              <TrendingUp size={12} className="text-green-600" />
            ) : (
              <TrendingDown size={12} className="text-red-600" />
            )}
            <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
              {percentage}%
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {price.unit}
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div className="font-medium text-sm">{price.market_city}</div>
          <div className="text-xs text-gray-600">{price.market_type}</div>
        </div>
      </TableCell>
      <TableCell>
        <div>
          {price.grade && (
            <div className="text-sm font-medium">{price.grade}</div>
          )}
          {price.package_type && (
            <div className="text-xs text-gray-600">{price.package_type}</div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm">
          {formatDate(price.report_date)}
        </div>
        <div className="text-xs text-gray-500">
          Stored: {formatDate(price.created_at)}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm">
          {price.origin || 'Regional'}
        </div>
      </TableCell>
    </TableRow>
  );
};
